import { EventEmitter } from '@angular/core';
import { Type, Expose, Exclude, instanceToInstance } from 'class-transformer';
import { intToDate, dateToInt } from '@saliente/library';


export class AccNoteListModel {
    recordIndex: number = 0;
    @Expose({ name: 'RecordNo' })
    recordNo: number;
    @Expose({ name: 'RecordType' })
    recordType: string;
    @Expose({ name: 'Number' })
    number: string;


    @Expose({ name: 'Date' })
    public dateInt: number;
    @Exclude()
    private _dateEx: Date;
    @Exclude()
    public get dateEx(): Date {
        if (!this._dateEx && this.dateInt) {
            this._dateEx = intToDate(this.dateInt);
        }
        return this._dateEx;
    }
    public set dateEx(value: Date) {
        if (this._dateEx != value) {
            this._dateEx = value;
            this.dateInt = dateToInt(value);
        }
    }

    @Expose({ name: 'DueDate' })
    public dueDateInt: number;
    @Exclude()
    private _dueDateEx: Date;
    @Exclude()
    public get dueDateEx(): Date {
        if (!this._dueDateEx && this.dueDateInt) {
            this._dueDateEx = intToDate(this.dueDateInt);
        }
        return this._dueDateEx;
    }
    public set dueDateEx(value: Date) {
        if (this._dueDateEx != value) {
            this._dueDateEx = value;
            this.dueDateInt = dateToInt(value);
        }
    }

    @Expose({ name: 'Comments' })
    comments: string;
    @Expose({ name: 'Partner' })
    partner: string;
    @Expose({ name: 'PartnerTaxCode' })
    partnerTaxCode: string;
    @Expose({ name: 'LedgerCode' })
    ledgerCode: string;
    @Expose({ name: 'CreditAccount' })
    creditAccount: string;
    @Expose({ name: 'DebitAccount' })
    debitAccount: string;
    @Expose({ name: 'Employee' })
    employee: string;
    @Expose({ name: 'ValueCurrency' })
    valueCurrency: number;
    @Expose({ name: 'ValueRON' })
    valueRON: number;
    @Expose({ name: 'TotalDocumentValue' })
    totalDocumentValue: number;
    @Expose({ name: 'Currency' })
    currency: string;
    @Expose({ name: 'ExchangeRate' })
    exchangeRate: number;
    @Expose({ name: 'VATType' })
    vatType: string;
    @Expose({ name: 'VATQuota' })
    vatQuota: number;
    @Expose({ name: 'VATOnPaymentDetail' })
    vatOnPaymentDetail: number;
    @Expose({ name: 'UserName' })
    userName: string;
    @Expose({ name: 'SAFTCode' })
    saftCode: string;
}

export class AccNoteDetailModel {

    //#region fields

    @Expose({ name: 'LineId' })
    public lineId: number;

    @Expose({ name: 'LineNumber' })
    public lineNumber: number;

    @Exclude()
    @Expose({ name: 'LedgerCode' })
    public ledgerCode: string;

    @Expose({ name: 'CreditAccount' })
    public creditAccount: string;

    @Expose({ name: 'DebitAccount' })
    public debitAccount: string;

    @Expose({ name: 'ValueCurrency' })
    public valueCurrency: number;

    @Expose({ name: 'ValueRON' })
    public valueRON: number;

    @Expose({ name: 'VATType' })
    public vatType: string;

    @Expose({ name: 'VATQuota' })
    public vatQuota: number;

    @Expose({ name: 'PartnerEid' })
    public partnerEid: string;

    @Expose({ name: 'Partner' })
    public partner: string;

    @Expose({ name: 'PartnerCode' })
    public partnerCode: string;

    @Expose({ name: 'PartnerTaxCode' })
    public partnerTaxCode: string;

    @Expose({ name: 'EmployeeCode' })
    public employeeCode: string;

    @Expose({ name: 'Employee' })
    public employee: string;

    @Expose({ name: 'VATOnPaymentDetail' })
    public vatOnPaymentInt: number;

    @Exclude()
    public get vatOnPayment(): boolean {
        return !!this.vatOnPaymentInt;
    }
    public set vatOnPayment(value: boolean) {
        this.vatOnPaymentInt = value ? 1 : 0;
    }

    @Expose({ name: 'SAFTCode' })
    public saftCode: string;

    @Expose({ name: 'Comments' })
    public comments: string;

    @Expose({ name: 'Dimension1' })
    public dimension1: string;

    @Expose({ name: 'Dimension2' })
    public dimension2: string;

    @Expose({ name: 'Dimension3' })
    public dimension3: string;

    @Expose({ name: 'Dimension4' })
    public dimension4: string;

    @Expose({ name: 'Dimension5' })
    public dimension5: string;

    //#endregion fields

    //#region properties

    //#region metadataInfo

    @Expose({ name: 'Extra' })
    public metadataInfoString: string;

    @Exclude()
    public metadataInfoEx: any = {};

    //#endregion metadataInfo

    @Exclude()
    public isEmpty: boolean = false;

    //#endregion

    //#region methods

    public onAfterLoad() {
        this.isEmpty = false;

        let metadataInfoEx: any = {};
        if (this.metadataInfoString) {
            try {
                metadataInfoEx = JSON.parse(this.metadataInfoString);
            } catch { }
        }
        this.metadataInfoEx = metadataInfoEx;
    }

    public onBeforeSave() {
        if (this.metadataInfoEx) {
            this.metadataInfoString = JSON.stringify(this.metadataInfoEx);
        } else {
            this.metadataInfoString = '';
        }
    }

    //#endregion

    static typeProvider() {
        return AccNoteDetailModel;
    }
}
export class AccNoteHeaderModel {

    @Exclude()
    public collapsed: boolean = true;

    //#region fields

    @Expose({ name: 'RecordNo' })
    public recordNo: number;

    @Expose({ name: 'ImportedAutoNumber' })
    public importedAutoNumber: number;

    @Expose({ name: 'RecordType' })
    public recordType: string;

    @Expose({ name: 'Number' })
    public number: string;

    @Expose({ name: 'Date' })
    public dateInt: number;
    @Exclude()
    private _date: Date;
    @Exclude()
    public get date(): Date {
        if (!this._date && this.dateInt) {
            this._date = intToDate(this.dateInt);
        }
        return this._date;
    }
    public set date(value: Date) {
        if (this._date != value) {
            this._date = value;
            this.dateInt = dateToInt(value);
        }
    }

    @Expose({ name: 'DueDate' })
    public dueDateInt: number;
    @Exclude()
    private _dueDate: Date;
    @Exclude()
    public get dueDate(): Date {
        if (!this._dueDate && this.dueDateInt) {
            this._dueDate = intToDate(this.dueDateInt);
        }
        return this._dueDate;
    }
    public set dueDate(value: Date) {
        if (this._dueDate != value) {
            this._dueDate = value;
            this.dueDateInt = dateToInt(value);
        }
    }

    @Expose({ name: 'DocumentDate' })
    public documentDateInt: number;
    @Exclude()
    private _documentDate: Date;
    @Exclude()
    public get documentDate(): Date {
        if (!this._documentDate && this.documentDateInt) {
            this._documentDate = intToDate(this.documentDateInt);
        }
        return this._documentDate;
    }
    public set documentDate(value: Date) {
        if (this._documentDate != value) {
            this._documentDate = value;
            this.documentDateInt = dateToInt(value);
        }
    }

    @Expose({ name: 'Currency' })
    public currency: string;

    @Expose({ name: 'ExchangeRate' })
    public exchangeRate: number;

    @Expose({ name: 'VATOnPaymentHeader' })
    public vatOnPaymentInt: number;

    @Expose({ name: 'DocumentTotalValue' })
    public documentTotalValue: number;

    @Exclude()
    public get vatOnPayment(): boolean {
        return !!this.vatOnPaymentInt;
    }
    public set vatOnPayment(value: boolean) {
        this.vatOnPaymentInt = value ? 1 : 0;
    }

    @Expose({ name: 'HeaderComments' })
    public comments: string;

    @Expose({ name: 'UserName' })
    public userName: string;

    @Expose({ name: 'CodTIRO' })
    public codTIRO: string;

    @Expose({ name: 'SupplierLocation' })
    public supplierLocation: boolean;
    //#endregion

    //#region details

    @Type(AccNoteDetailModel.typeProvider)
    @Expose({ name: 'Details' })
    public details: AccNoteDetailModel[] = [];

    //#endregion

    //#region metadataInfoEx

    @Expose({ name: 'metadataInfo' })
    public metadataInfoString: string;
    @Exclude()
    public metadataInfoEx: any;


    //#endregion metadataInfoEx

    @Exclude()
    public templateEx: any;

    public onAfterLoad() {
        let metadataInfoEx: any = {};
        if (this.metadataInfoString) {
            try {
                metadataInfoEx = JSON.parse(this.metadataInfoString);
            } catch { }
        }
        this.metadataInfoEx = metadataInfoEx;
        metadataInfoEx.template = metadataInfoEx.template || 'default';

        this.onAfterDetailsLoaded();
    }

    public onAfterDetailsLoaded() {
        if (this.details) {
            this.details.forEach((detail) => {
                detail.onAfterLoad();
            });
        }
    }

    public onBeforeSave() {
        const self = this;

        if (this.metadataInfoEx) {
            this.metadataInfoString = JSON.stringify(this.metadataInfoEx);
        } else {
            this.metadataInfoString = '';
        }

        if (self.details) {
            for (var len = this.details.length, idx = len - 1; idx >= 0; idx--) {
                var detail = this.details[idx];
                if (detail.isEmpty) {
                    // AM. 25.08.2021. task 5014: no longer need to remove the empty lines from the model because they have not been added before
                    // this.details.splice(idx, 1);
                } else {
                    detail.onBeforeSave();
                }
            }
        }
    }

    static typeProvider() {
        return AccNoteHeaderModel;
    }
}

export class AccNoteMetadataModel {

    public clientEid: string;
    public externalId: string;
    public isVirtual: boolean;
    @Expose({ name: 'metadataInfo' })
    public metadataInfoString: string;
    @Exclude()
    public metadataInfoEx: any;

    public onAfterLoad() {
        let metadataInfoEx: any = {};
        if (this.metadataInfoString) {
            try {
                metadataInfoEx = JSON.parse(this.metadataInfoString);
            } catch { }
        }
        this.metadataInfoEx = metadataInfoEx;
    }

    public onBeforeSave() {
        if (this.metadataInfoEx) {
            this.metadataInfoString = JSON.stringify(this.metadataInfoEx);
        } else {
            this.metadataInfoString = '';
        }
    }

    static typeProvider() {
        return AccNoteMetadataModel;
    }
}

export class AccNoteGroupModel {
    public documentEid: string;

    @Exclude()
    public file: any;

    @Type(AccNoteMetadataModel.typeProvider)
    public metadata: AccNoteMetadataModel;

    @Type(AccNoteHeaderModel.typeProvider)
    @Expose({ name: 'accountingNotes' })
    public headers: AccNoteHeaderModel[] = [];


    @Exclude()
    public expertDocuments: string[] = [];

    @Exclude()
    get expertDocumentsIndex() {
        return this.expertDocuments.indexOf(this.documentEid);
    }

    @Exclude()
    get expertDocumentsDisplayIndex() {
        return this.expertDocumentsIndex + 1;
    }

    public onAfterLoad() {
        this.metadata.onAfterLoad();
        if (this.headers && this.headers.length) {
            this.headers.forEach((header) => {
                header.onAfterLoad();
            });
            this.headers[0].collapsed = false;
        }
    }

    public onBeforeSave() {
        this.headers.forEach((header) => {
            header.onBeforeSave();
        });
        this.metadata.onBeforeSave();
    }
}

export class DimensionValuesModel {
    public code: string;
    public name: string;

    public get fullName(): string {
        return `${this.code} - ${this.name}`;
    }
}

export class DimensionConfigurationsModel {
    public dimensionId: number;
    public dimensionName: string;
    public displayOrder: number;
    public charSize: number;
    @Type(() => DimensionValuesModel)
    public dimensionValues: DimensionValuesModel[]
}

export class SaftCodesModel {
    public saftCode: string;
}

export class SaftDefaultCodeRequestModel {
    public transactionType: string;
    public debit: string;
    public credit: string;
    public vatOption: string;
    public quotaValue: number;
    public partnerCode: string;
    public isVOP: boolean;
}